import React, { useState } from "react";
import type { DeliverySchedule } from "../types";
import { ActualInputModal } from "./modal/actualInputModal";
import { ApprovalModal } from "./modal/approvalModal";
import { TableButtonEdit, TableButtonCommit } from "./tableButton";

const SCHEDULE_TITLE = "納入予定";
const RESULT_TITLE = "納入実績";
const OPERATION_TITLE = "データ操作";
const SCHEDULE_HEADERS = ["薬品名", "日付", "時刻", "数量", "単位"];
const RESULT_HEADERS = ["日付", "時刻", "数量"];
const OPERATION_HEADERS = ["実績入力", "確認"];

interface Props {
  /** 本日の納入予定か */
  isToday: boolean;
  /** 納品予定情報 */
  schedules: DeliverySchedule[];
  /** 実績入力ボタンハンドラ */
  onResultInput: (
    schedule: DeliverySchedule,
    date: string,
    time: string,
    volume: number
  ) => Promise<void>;
  /** 確認ボタンハンドラ */
  onApprove: (schedule: DeliverySchedule) => Promise<void>;
  /** 編集可否 */
  canEdit: boolean;
  /** 確認可否 */
  canCommit: boolean;
}

/**
 * 納品予定テーブル表示
 * @param props
 * @returns
 */
const DeliveryTable: React.FC<Props> = ({
  isToday,
  schedules,
  onResultInput,
  onApprove,
  canEdit,
  canCommit,
}) => {
  // 実績入力中の納入予定
  const [inputSchedule, setInputSchedule] = useState<DeliverySchedule | null>(null);
  // 確認中の納入予定
  const [approveSchedule, setApproveSchedule] = useState<DeliverySchedule | null>(null);

  // 納入実績 実績の登録処理
  const handleResultInput = async (date: string, time: string, volume: number) => {
    await onResultInput(inputSchedule, date, time, volume);
    setInputSchedule(null);
  };

  // 確認の登録処理
  const handleApproveSubmit = async () => {
    await onApprove(approveSchedule);
    setApproveSchedule(null);
  };

  // 本日の日付をYYYY/MM/DD(W)形式で取得
  const now: Date = new Date(Date.now());
  const formatter = new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const formattedNowDate = formatter.format(now);

  let filteredSchedules: DeliverySchedule[];
  // isTodayがtrueであれば、本日以前の納入予定を表示
  if (isToday) {
    filteredSchedules = schedules.filter(schedule => {
      return schedule.planDate <= formattedNowDate;
    });
    // isTodayがfalseであれば、明日以降の納入予定を表示
  } else {
    filteredSchedules = schedules.filter(schedule => {
      return schedule.planDate > formattedNowDate;
    });
  }

  return (
    // 縦スクロールのためにdivを用意
    <div className="delivery-table">
      <table>
        <thead>
          <tr>
            <th colSpan={SCHEDULE_HEADERS.length}>{SCHEDULE_TITLE}</th>
            <th colSpan={RESULT_HEADERS.length}>{RESULT_TITLE}</th>
            {canEdit && <th colSpan={OPERATION_HEADERS.length}>{OPERATION_TITLE}</th>}
          </tr>
          <tr>
            {/* 納入予定配下ヘッダー */}
            {SCHEDULE_HEADERS.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
            {/* 納入実績配下ヘッダー */}
            {RESULT_HEADERS.map((h, i) => (
              <th key={i + 10}>{h}</th>
            ))}
            {/* データ操作配下ヘッダー */}
            {canEdit && OPERATION_HEADERS.map((h, i) => <th key={i + 20}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {filteredSchedules.map(s => {
            const hasResult = s.resultDate && s.resultTime && s.resultVolume;
            const isResultDelay = s.isDelayedTime && !hasResult;
            const resultClassName = isResultDelay ? "delayed" : "";
            const approveClassName = hasResult && !s.isApproved && s.isDelayedDate ? "delayed" : "";
            return (
              <tr key={`${s.chemicalId}${s.orderAdddatetime}`}>
                <td>{s.chemicalName || "不明な薬品名"}</td>
                <td>{s.planDate}</td>
                <td>
                  {s.planTime === "00:00" && s.planTimeEnd === "00:00"
                    ? "指定なし"
                    : s.planTime === "00:00" && s.planTimeEnd === "12:00"
                    ? "AM"
                    : s.planTime === "12:01" && s.planTimeEnd === "23:59"
                    ? "PM"
                    : `${s.planTime}${s.planTimeEnd ? `〜${s.planTimeEnd}` : ""}`}
                </td>
                <td>{s.planVolume}</td>
                <td>{s.planUnit || "不明な単位"}</td>
                <td>{hasResult ? s.resultDate : ""}</td>
                <td>{hasResult ? s.resultTime : ""}</td>
                <td>{hasResult ? s.resultVolume : ""}</td>
                {canEdit && (
                  <td className={resultClassName}>
                    {!s.isApproved && <TableButtonEdit onClick={() => setInputSchedule(s)} />}
                  </td>
                )}
                {canEdit && (
                  <td className={approveClassName}>
                    {!s.isApproved && hasResult && canCommit && (
                      <TableButtonCommit onClick={() => setApproveSchedule(s)} />
                    )}
                    {s.isApproved && hasResult && "済"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 実績入力モーダル */}
      {inputSchedule && (
        <ActualInputModal
          schedule={inputSchedule}
          onClose={() => setInputSchedule(null)}
          onSubmit={handleResultInput}
        />
      )}

      {/* 確認モーダル */}
      {approveSchedule && (
        <ApprovalModal
          schedule={approveSchedule}
          onClose={() => setApproveSchedule(null)}
          onApprove={handleApproveSubmit}
        />
      )}
    </div>
  );
};

export default DeliveryTable;
